import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  API_BASE_URL, HERO_TABS, HERO_COLUMNS,
  HERO_MESSAGES, getSubmitButtonLabel,
  getContentUpdateDescription, getSheetUrl, autoGenerateUrls,
  HERO_DUPLICATE_CHECK, findDuplicates,
} from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminHeroEditor({ user, section, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("tabs");
  const [tabOrder, setTabOrder] = useState(HERO_TABS.map(t => t.id));
  const dragRef = useRef(null);
  const tabBarRef = useRef(null);
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    const wheelHandler = (e) => {
      if (e.deltaY !== 0) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", wheelHandler, { passive: false });
    return () => el.removeEventListener("wheel", wheelHandler);
  }, []);
  const [sectionInfo, setSectionInfo] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successTitle, setSuccessTitle] = useState("");
  const [arraySearch, setArraySearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState([]);
  const [duplicateActionLoading, setDuplicateActionLoading] = useState(false);
  const snapshotRef = useRef(null);

  useEffect(() => {
    setArraySearch("");
    setSearchOpen(false);
  }, [activeTab]);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data && data.section) {
        const c = data.section.content;
        setContent(c);
        snapshotRef.current = JSON.stringify(c);
        setSectionInfo({ version: data.section.version, updatedAt: data.section.updatedAt });
      } else {
        setMessage(HERO_MESSAGES.apiError);
      }
    } catch (err) {
      setMessage(HERO_MESSAGES.connectionError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSheet = () => {
    window.open(getSheetUrl(section.key), "_blank", "noopener,noreferrer");
  };

  const handlePushToSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/push-sheet`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const text = await res.text();
        setMessage(`Push failed (${res.status}): ${text.substring(0, 200)}`);
        return;
      }
      const data = await res.json();
      setMessage(data.success ? "Pushed to Google Sheet successfully!" : data.error || "Push failed.");
    } catch (err) {
      setMessage("Push error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePullFromSheet = async () => {
    const token = sessionStorage.getItem("tvc_token");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/content/${section.key}/pull-sheet`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setContent(data.content);
        setMessage("Pulled from Google Sheet. Review changes and click Publish to go live.");
      } else {
        setMessage(data.error || "Pull failed.");
      }
    } catch (err) {
      setMessage("Pull error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (arr[index]) {
        arr[index] = { ...arr[index], [key]: value };
      }
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowBefore = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      if (arr.length > 0) {
        for (const key of Object.keys(arr[0])) {
          emptyRow[key] = "";
        }
      } else {
        const cols = HERO_COLUMNS[field];
        if (cols) {
          for (const col of cols) {
            emptyRow[col.key] = "";
          }
        }
      }
      arr.splice(index, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleAddRowAfter = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      const emptyRow = {};
      if (arr.length > 0) {
        for (const key of Object.keys(arr[0])) {
          emptyRow[key] = "";
        }
      } else {
        const cols = HERO_COLUMNS[field];
        if (cols) {
          for (const col of cols) {
            emptyRow[col.key] = "";
          }
        }
      }
      arr.splice(index + 1, 0, emptyRow);
      updated[field] = arr;
      return updated;
    });
  };

  const handleDeleteRow = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      updated[field] = (updated[field] || []).filter((_, i) => i !== index);
      return updated;
    });
  };

  const handleMoveRowUp = (field, index) => {
    if (index === 0) return;
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleMoveRowDown = (field, index) => {
    setContent((prev) => {
      const updated = { ...prev };
      const arr = [...(updated[field] || [])];
      if (index >= arr.length - 1) return prev;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      updated[field] = arr;
      return updated;
    });
  };

  const handleFieldChange = (field, value) => {
    setContent((prev) => {
      if (field.startsWith("settings.")) {
        const parts = field.split(".");
        return { ...prev, settings: { ...prev.settings, [parts[1]]: value } };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async () => {
    const dupes = findDuplicates(content, HERO_DUPLICATE_CHECK);
    if (dupes.length > 0) {
      setDuplicateInfo(dupes);
      setShowDuplicateModal(true);
      return;
    }
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleConfirmSubmit = async () => {
    if (!noteText.trim()) return;
    setShowNoteModal(false);
    await doSubmit(noteText.trim());
  };

  const handleContinueAnyway = async () => {
    setShowDuplicateModal(false);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit();
  };

  const handleRemoveDuplicates = async () => {
    setDuplicateActionLoading(true);
    const cleanContent = JSON.parse(JSON.stringify(content));
    const sorted = [...duplicateInfo].sort((a, b) => {
      if (a.sectionIndex !== undefined && b.sectionIndex !== undefined) {
        if (a.sectionIndex !== b.sectionIndex) return b.sectionIndex - a.sectionIndex;
      }
      return b.duplicateIndex - a.duplicateIndex;
    });
    for (const dup of sorted) {
      const arr = [...(cleanContent[dup.field] || [])];
      if (arr[dup.duplicateIndex]) {
        arr.splice(dup.duplicateIndex, 1);
        cleanContent[dup.field] = arr;
      }
    }
    setContent(cleanContent);
    setShowDuplicateModal(false);
    setDuplicateActionLoading(false);
    snapshotRef.current = JSON.stringify(cleanContent);
    const role = user?.role || "Staff";
    if (role !== "Admin") {
      setNoteText("");
      setShowNoteModal(true);
      return;
    }
    await doSubmit("", cleanContent);
  };

  const doSubmit = async (submitterNote, overrideContent) => {
    setMessage("");
    setSaving(true);
    const token = sessionStorage.getItem("tvc_token");
    const submitPayload = overrideContent || { ...content };
    const estimateSize = new Blob([JSON.stringify(submitPayload)]).size;
    if (estimateSize > 4 * 1024 * 1024) {
      setMessage(`Payload too large (${(estimateSize / 1024 / 1024).toFixed(1)} MB). Reduce image sizes before submitting.`);
      setSaving(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/content/submit-change`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          description: getContentUpdateDescription(section.label),
          changes: {
            type: "content_update",
            sectionKey: section.key,
            content: submitPayload,
            ...(submitterNote ? { submitterNote } : {}),
          },
          sectionKey: section.key,
        }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { error: text }; }
      if (res.ok) {
        snapshotRef.current = JSON.stringify(content);
        const role = user?.role || "Staff";
        setSuccessTitle(role === "Admin" ? "Changes Published" : "Request made");
        setShowSuccessOverlay(true);
      } else {
        setMessage(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setMessage(`Network error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderArrayEditor = (field, columns, searchVal, searchOpenState, setSearchVal, setSearchOpenState, searchRefField) => {
    const items = content?.[field] || [];
    return (
      <div className="AdminCardMb">
        <div className="AdminSectionHeader">
          <h4 className="AdminSectionTitle">
            {field.replace(/([A-Z])/g, " $1").trim()}
          </h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div className={`AdminPillSearchWrap ${searchOpenState ? 'is-open' : ''}`}>
              <span className="AdminPillSearchIcon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </span>
              <input
                ref={searchRefField}
                type="text"
                className="AdminPillSearch"
                placeholder="Search..."
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                onBlur={() => { setSearchVal(""); setTimeout(() => setSearchOpenState(false), 200) }}
              />
              {!searchOpenState && (
                <button className="AdminPillSearchOverlay" onClick={() => setSearchOpenState(true)} type="button" aria-label="Search" />
              )}
            </div>
            <button
              className="AdminButton AdminButtonSmall AdminButtonSecondary AdminAddRowBtn"
              onClick={() => handleAddRowAfter(field, items.length - 1)}
            >
              + Add Row
            </button>
          </div>
        </div>
        <div className="AdminTableScroll">
          <table className="AdminTable AdminTableEditor">
            <thead>
              <tr>
                <th className="AdminThOrder">Order</th>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th className="AdminThActions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items
                .map((item, idx) => ({ item, idx }))
                .filter(({ item }) => {
                  if (!searchVal.trim()) return true;
                  return columns.some((col) =>
                    String(item[col.key] || "").toLowerCase().includes(searchVal.trim().toLowerCase())
                  );
                })
                .map(({ item, idx }) => (
                <tr
                  key={idx}
                  draggable="true"
                  onDragStart={(e) => {
                    dragRef.current = { type: "array", field, fromIndex: idx };
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", JSON.stringify(dragRef.current));
                    e.currentTarget.style.opacity = "0.4";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    e.currentTarget.style.outline = "2px dashed #3b82f6";
                    e.currentTarget.style.outlineOffset = "-2px";
                  }}
                  onDragLeave={(e) => { e.currentTarget.style.outline = ""; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    if (!dragRef.current || dragRef.current.type !== "array") { dragRef.current = null; return; }
                    const { field: fromField, fromIndex } = dragRef.current;
                    if (fromField !== field || fromIndex === idx) { dragRef.current = null; return; }
                    setContent((prev) => {
                      const updated = { ...prev };
                      const arr = [...(updated[field] || [])];
                      const [moved] = arr.splice(fromIndex, 1);
                      arr.splice(idx, 0, moved);
                      updated[field] = arr;
                      return updated;
                    });
                    dragRef.current = null;
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "";
                    e.currentTarget.style.outline = "";
                    dragRef.current = null;
                  }}
                >
                  <td>
                    <div className="AdminOrderBtns">
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowUp(field, idx)} disabled={idx === 0} title="Move up">&#8593;</button>
                      <button className="AdminMoveBtn" onClick={() => handleMoveRowDown(field, idx)} disabled={idx === items.length - 1} title="Move down">&#8595;</button>
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.type === "textarea" ? (
                        <textarea
                          className="AdminInput AdminTableInput"
                          style={{ minHeight: 60, resize: "vertical" }}
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      ) : (
                        <input
                          className="AdminInput AdminTableInput"
                          value={item[col.key] || ""}
                          onChange={(e) => handleArrayChange(field, idx, col.key, e.target.value)}
                          placeholder={col.label}
                        />
                      )}
                    </td>
                  ))}
                  <td>
                    <div className="AdminActionBtns">
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowBefore(field, idx)} title="Add row before">&#9650;+</button>
                      <button className="AdminIconBtn AdminIconBtnBlue" onClick={() => handleAddRowAfter(field, idx)} title="Add row after">&#9660;+</button>
                      <button className="AdminIconBtn AdminIconBtnRed" onClick={() => handleDeleteRow(field, idx)} title="Delete row">&#10005;</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && (
          <div className="AdminEmptyState">{HERO_MESSAGES.emptyArray}</div>
        )}
        {items.length > 0 && searchVal.trim() && !items.some((item) =>
          columns.some((col) =>
            String(item[col.key] || "").toLowerCase().includes(searchVal.trim().toLowerCase())
          )
        ) && (
          <div className="AdminEmptyState">No results found for "{searchVal}"</div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="AdminEmptyState">{HERO_MESSAGES.loading}</div>;
  }

  if (!content) {
    return (
      <div>
        <button className="AdminButton AdminButtonSmall AdminButtonSecondary AdminBackBtn" onClick={onBack}>
          &larr; Back to Content Manager
        </button>
        <div className="AdminEmptyState">{HERO_MESSAGES.noContent}</div>
      </div>
    );
  }

  const isDirty = JSON.stringify(content) !== snapshotRef.current;
  const orderedTabs = [...HERO_TABS].sort((a, b) => tabOrder.indexOf(a.id) - tabOrder.indexOf(b.id));

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
        <h3 className="AdminFooterH3">
          Edit Hero Content
          {activeTab !== "data" && Array.isArray(content?.[activeTab]) && (
            <span className="AdminCountBadge">{content[activeTab].length}</span>
          )}
        </h3>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
          <button className="AdminButton AdminButtonSmall AdminSubmitBtn" onClick={handleSubmit} disabled={!isDirty || saving} style={{ whiteSpace: "nowrap" }}>
            {saving ? "Publishing..." : getSubmitButtonLabel(user?.role)}
          </button>
          <button className="AdminButton AdminButtonSmall AdminSheetBtn" onClick={handleOpenSheet}>
            Google Sheet
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePushToSheet} disabled={loading} title="Push to Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </button>
          <button className="AdminSheetIconBtn" onClick={handlePullFromSheet} disabled={loading} title="Pull from Sheet">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      {message && (
        <p className={`AdminMsgBox ${message.includes("success") ? "AdminSuccess" : "AdminError"}`}>
          {message}
        </p>
      )}

      <div className="AdminTabBar" ref={tabBarRef} style={{ display: "flex", gap: 0, borderBottom: "2px solid #e2e8f0", marginBottom: 16 }}>
        {orderedTabs.map((tab) => (
          <button
            key={tab.id}
            draggable
            onDragStart={(e) => { dragRef.current = tab.id; e.target.style.opacity = '0.5'; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromId = dragRef.current;
              if (!fromId || fromId === tab.id) return;
              const newOrder = [...tabOrder];
              const fromIdx = newOrder.indexOf(fromId);
              const toIdx = newOrder.indexOf(tab.id);
              newOrder.splice(fromIdx, 1);
              newOrder.splice(toIdx, 0, fromId);
              setTabOrder(newOrder);
            }}
            onDragEnd={(e) => { e.target.style.opacity = '1'; dragRef.current = null; }}
            className={`AdminTabBtn ${activeTab === tab.id ? "AdminTabBtnActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none", background: activeTab === tab.id ? "#1B2A4A" : "transparent",
              color: activeTab === tab.id ? "#fff" : "#64748b", cursor: "grab", fontSize: 14, fontWeight: 600,
              borderRadius: "8px 8px 0 0", transition: "all 0.2s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "data" && (
        <div className="AdminCard">
          <div className="AdminDataHeader">
            <h4 className="AdminSectionTitle">Data</h4>
          </div>
          <div className="AdminDataInfo">
            <div>
              <span className="AdminDataLabel">Last Updated</span>
              <p className="AdminDataValue">
                {sectionInfo?.updatedAt ? new Date(sectionInfo.updatedAt).toLocaleString() : "Not published"}
              </p>
            </div>
            <div>
              <span className="AdminDataLabel">Section Key</span>
              <p className="AdminDataValue">{section?.key || "hero"}</p>
            </div>
          </div>
          <textarea
            className="AdminJsonEditor"
            value={JSON.stringify(content, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                setContent(parsed);
                setMessage("");
              } catch {
                setMessage(HERO_MESSAGES.invalidJson);
              }
            }}
          />
        </div>
      )}

      {activeTab === "tabs" && (
        <div>
          <div className="AdminCardMb">
            <div className="AdminSectionHeader">
              <h4 className="AdminSectionTitle">Hero Settings</h4>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '0 0 16px 0' }}>
              <div className="AdminFormGroup">
                <label className="AdminLabel">Hero Title</label>
                <input
                  className="AdminInput"
                  value={content.heroTitle || ""}
                  onChange={(e) => handleFieldChange("heroTitle", e.target.value)}
                  placeholder="Hero title"
                />
              </div>
              <div className="AdminFormGroup">
                <label className="AdminLabel">Hero Subtitle</label>
                <input
                  className="AdminInput"
                  value={content.heroSubtitle || ""}
                  onChange={(e) => handleFieldChange("heroSubtitle", e.target.value)}
                  placeholder="Hero subtitle"
                />
              </div>
              <div className="AdminFormGroup">
                <label className="AdminLabel">Search Placeholder</label>
                <input
                  className="AdminInput"
                  value={content.searchPlaceholder || ""}
                  onChange={(e) => handleFieldChange("searchPlaceholder", e.target.value)}
                  placeholder="Search placeholder text"
                />
              </div>
            </div>
          </div>
          {renderArrayEditor("tabs", HERO_COLUMNS.tabs, arraySearch, searchOpen, setArraySearch, setSearchOpen, searchRef)}
        </div>
      )}

      {showNoteModal && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 480 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 className="AdminModalTitle" style={{ margin: 0 }}>Describe Your Changes</h3>
              <button className="AdminCloseBtn" onClick={() => setShowNoteModal(false)}>&times;</button>
            </div>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 12px 0" }}>
              Please provide a note describing what changes you made. This is required before submitting.
            </p>
            <textarea
              className="AdminTextarea"
              style={{ minHeight: 100, width: "100%", boxSizing: "border-box" }}
              placeholder="Describe what changed..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              autoFocus
            />
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={handleConfirmSubmit} disabled={!noteText.trim() || saving}
                style={{ opacity: !noteText.trim() ? 0.5 : 1 }}>
                {saving ? "Submitting..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessOverlay && (
        <div className="AdminModal" onClick={() => setShowSuccessOverlay(false)}>
          <div className="AdminModalContent" style={{ maxWidth: 400, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>
              {successTitle === "Changes Published" ? "\u2705" : "\uD83D\uDCEB"}
            </div>
            <h3 className="AdminModalTitle" style={{ marginBottom: 4 }}>{successTitle}</h3>
            <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 20px 0" }}>
              {successTitle === "Changes Published" ? "Your content changes have been published live." : "Your change request has been submitted for approval."}
            </p>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={() => setShowSuccessOverlay(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && duplicateInfo.length > 0 && (
        <div className="AdminModal">
          <div className="AdminModalContent" style={{ maxWidth: 520, position: "relative" }}>
            <button onClick={() => setShowDuplicateModal(false)} style={{ position: "absolute", top: 8, right: 12, background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b", lineHeight: 1 }} title="Close">&times;</button>
            <div style={{ fontSize: 36, textAlign: "center", marginBottom: 8 }}>&#9888;</div>
            <h3 className="AdminModalTitle" style={{ textAlign: "center", margin: "0 0 4px 0" }}>Duplicate Entries Detected</h3>
            <p style={{ fontSize: 13, color: "#64748b", textAlign: "center", margin: "0 0 16px 0" }}>
              The following duplicate entries were found. Choose how to proceed.
            </p>
            <div style={{ background: "#fff8f0", border: "1px solid #fecaca", borderRadius: 8, padding: 12, marginBottom: 16 }}>
              {(() => {
                const byTab = {};
                for (const d of duplicateInfo) {
                  if (!byTab[d.tabId]) byTab[d.tabId] = { label: d.tabLabel, items: [] };
                  byTab[d.tabId].items.push(d.duplicateValue);
                }
                return Object.entries(byTab).map(([tabId, info]) => (
                  <div key={tabId} style={{ marginBottom: 8, fontSize: 14, textAlign: "center" }}>
                    <strong style={{ color: "#1B2A4A" }}>{info.label}</strong>
                    <span style={{ color: "#475569" }}> &mdash; Duplicate{info.items.length > 1 ? "s" : ""}: </span>
                    <span style={{ color: "#1B2A4A" }}>{info.items.join(", ")}</span>
                  </div>
                ));
              })()}
            </div>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button className="AdminButton AdminSubmitBtn" onClick={handleRemoveDuplicates} disabled={duplicateActionLoading}
                style={{ background: "#dc2626", borderColor: "#dc2626" }}>
                {duplicateActionLoading ? "Removing..." : "Remove Duplicate Rows"}
              </button>
              <button className="AdminButton AdminSubmitBtn" onClick={handleContinueAnyway} disabled={duplicateActionLoading}>
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminHeroEditor;
